// src/components/AdvancedStockCharts.js
import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
//  CandlestickChart,
//  Candlestick,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ReferenceLine
} from 'recharts';
import CandlestickChart from './CandlestickChart';
import ProfessionalCandlestickChart from './ProfessionalCandlestickChart';
import { tigerWebSocketService } from '../../services/tigerWebSocket';
import { 
  Calendar, 
  BarChart3, 
  TrendingUp,
  Download,
  RefreshCw
} from 'lucide-react';

const AdvancedStockCharts = ({ symbol, darkMode }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState('candlestick');
  const [timeframe, setTimeframe] = useState('1d');
  const [indicators, setIndicators] = useState(['sma', 'volume']);

  // Fetch historical data
  useEffect(() => {
    fetchHistoricalData();
  }, [symbol, timeframe]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (symbol) {
      tigerWebSocketService.onQuoteUpdate(symbol, handleRealTimeUpdate);
      return () => tigerWebSocketService.offQuoteUpdate(symbol);
    }
  }, [symbol]);

  const fetchHistoricalData = async () => {
    try {
      setLoading(true);
      // Replace with actual Tiger Trade historical data API call
      const mockData = generateMockChartData(getDataPointsForTimeframe(timeframe), timeframe);
      setChartData(mockData);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRealTimeUpdate = (quote) => {
    setChartData(prev => {
      const newData = [...prev];
      const lastCandle = { ...newData[newData.length - 1] };
      
      // Update last candle with real-time data
      lastCandle.close = quote.last;
      lastCandle.high = Math.max(lastCandle.high, quote.last);
      lastCandle.low = Math.min(lastCandle.low, quote.last);
      lastCandle.volume += quote.volume;
      
      newData[newData.length - 1] = lastCandle;
      return newData;
    });
  };

const handleTimeframeChange = (event) => {
  try {
    setTimeframe(event.target.value);
  } catch (error) {
    console.error('Error changing timeframe:', error);
  }
};

  const handleRefresh = () => {
    fetchHistoricalData();
  };

  const getDataPointsForTimeframe = (tf) => {
    switch (tf) {
      case '1m': return 100; // 100 minutes
      case '5m': return 100; // 100 * 5min = 500min ≈ 8hrs
      case '15m': return 100; // 100 * 15min = 1500min ≈ 25hrs
      case '30m': return 100; // 100 * 30min = 3000min ≈ 50hrs
      case '1h': return 100; // 100 hours
      case '4h': return 100; // 100 * 4hrs = 400hrs ≈ 17 days
      case '1d': return 100; // 100 days
      case '1w': return 52;  // 52 weeks
      case '1M': return 24;  // 24 months
      default: return 100;
    }
  };  

  const handleButtonClick = async (action, value, event) => {
    try {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      switch (action) {
        case 'changeTimeframe':
          setTimeframe(value);
          break;
        case 'changeChartType':
          setChartType(value);
          break;
        case 'refreshChart':
          await fetchHistoricalData();
          break;
        case 'toggleIndicator':
          setIndicators(prev => 
            prev.includes(value) 
              ? prev.filter(i => i !== value)
              : [...prev, value]
          );
          break;
        default:
          console.warn('Unknown chart action:', action);
      }
    } catch (error) {
      console.error('Error in chart button click:', error);
    }
  };  

  const renderChart = () => {
    switch (chartType) {
    //   case 'candlestick':
    //     return (
    //       <CandlestickChart data={chartData}>
    //         <CartesianGrid strokeDasharray="3 3" />
    //         <XAxis dataKey="time" />
    //         <YAxis />
    //         <Tooltip />
    //         <Candlestick dataKey="ohlc" />
    //         {indicators.includes('sma') && (
    //           <Line type="monotone" dataKey="sma20" stroke="#8884d8" dot={false} />
    //         )}
    //       </CandlestickChart>
    //     );
      case 'candlestick':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <CandlestickChart data={chartData} darkMode={darkMode} />
          </ResponsiveContainer>
        );    

      case 'professional-candlestick':
        return (
          <ProfessionalCandlestickChart 
            data={formatChartData(chartData)} 
            darkMode={darkMode} 
            symbol={symbol} 
          />
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => formatXAxis(value, timeframe)}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip timeframe={timeframe} />} />
              <Line 
                type="monotone" 
                dataKey="close" 
                stroke="#8884d8" 
                strokeWidth={2}
                dot={false}
              />
              {indicators.includes('sma') && (
                <Line 
                  type="monotone" 
                  dataKey="sma20" 
                  stroke="#ff7300" 
                  strokeWidth={1.5}
                  dot={false} 
                />
              )}
              {indicators.includes('ema') && (
                <Line 
                  type="monotone" 
                  dataKey="ema12" 
                  stroke="#00ff00" 
                  strokeWidth={1.5}
                  dot={false} 
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        );        
    //   case 'line':
    //     return (
    //       <LineChart data={formatChartData(chartData)}>
    //         <CartesianGrid strokeDasharray="3 3" />
    //         <XAxis dataKey="time" />
    //         <YAxis />
    //         <Tooltip />
    //         <Line type="monotone" dataKey="close" stroke="#8884d8" />
    //       </LineChart>
    //     );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                tickFormatter={(value) => formatXAxis(value, timeframe)}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip timeframe={timeframe} />} />
              <Area 
                type="monotone" 
                dataKey="close" 
                stroke="#8884d8" 
                fill="url(#colorGradient)" 
                fillOpacity={0.3}
              />
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        );    
    //   case 'area':
    //     return (
    //       <AreaChart data={chartData}>
    //         <CartesianGrid strokeDasharray="3 3" />
    //         <XAxis dataKey="time" />
    //         <YAxis />
    //         <Tooltip />
    //         <Area type="monotone" dataKey="close" stroke="#8884d8" fill="#8884d8" />
    //       </AreaChart>
    //     );
      case 'volume':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                tickFormatter={(value) => formatXAxis(value, timeframe)}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip timeframe={timeframe} />} />
              <Bar 
                dataKey="volume" 
                fill="#8884d8"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );    

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-96 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4">Loading chart data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow-lg p-6 ${
      darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
    }`}>
      {/* Chart Header */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold">{symbol} Chart</h3>
        <div className="flex space-x-4">
          {/* Timeframe Selector */}
            <select
              value={timeframe}
              onChange={(e) => handleButtonClick('changeTimeframe', e.target.value, e)} // Pass the event
              className={`px-3 py-2 rounded border ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
            <option value="1m">1 Minute</option>
            <option value="5m">5 Minutes</option>
            <option value="15m">15 Minutes</option>
            <option value="30m">30 Minutes</option>
            <option value="1h">1 Hour</option>
            <option value="4h">4 Hours</option>
            <option value="1d">1 Day</option>
            <option value="1w">1 Week</option>
          </select>

          {/* Chart Type Selector */}
          <select
            value={chartType}
            onChange={(e) => handleButtonClick('changeChartType', e.target.value, e)}
            className={`px-3 py-2 rounded border ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="line">Line</option>
            <option value="area">Area</option>
            <option value="candlestick">Candlestick (Basic)</option>
            <option value="professional-candlestick">Candlestick (Professional)</option>
          </select>

          <button
            onClick={(e) => handleButtonClick('refreshChart', null, e)}
            className={`flex items-center px-3 py-2 rounded text-sm ${
              darkMode
                ? 'bg-blue-700 text-white hover:bg-blue-600'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </button>          

          {/* Indicators Selector */}
          <select
            multiple
            value={indicators}
            onChange={(e) => handleButtonClick('toggleIndicator', Array.from(e.target.selectedOptions, option => option.value), e)}
            className={`px-3 py-2 rounded border ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="sma">SMA (20)</option>
            <option value="ema">EMA (12)</option>
            <option value="rsi">RSI</option>
            <option value="macd">MACD</option>
            <option value="volume">Volume</option>
          </select>
        </div>
      </div>
      <div className="h-96">
        {chartType === 'professional-candlestick' ? (
          <ProfessionalCandlestickChart 
            data={formatChartData(chartData)} 
            darkMode={darkMode} 
            symbol={symbol}
          />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>      

      {/* Chart Container
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div> */}

      {/* Volume Chart */}
      {indicators.includes('volume') && (
        <div className="h-32 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <Bar dataKey="volume" fill="#8884d8" />
              <XAxis dataKey="time" />
              <YAxis />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Technical Indicators
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-lg ${
          darkMode ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          <div className="text-sm text-gray-500">RSI (14)</div>
          <div className="text-xl font-bold">62.34</div>
        </div>
        <div className={`p-4 rounded-lg ${
          darkMode ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          <div className="text-sm text-gray-500">MACD</div>
          <div className="text-xl font-bold">1.23</div>
        </div>
        <div className={`p-4 rounded-lg ${
          darkMode ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          <div className="text-sm text-gray-500">Stochastic</div>
          <div className="text-xl font-bold">75.89</div>
        </div>
        <div className={`p-4 rounded-lg ${
          darkMode ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          <div className="text-sm text-gray-500">Volatility</div>
          <div className="text-xl font-bold">2.45%</div>
        </div>
      </div> */}
      {/* Technical Indicators */}
      <div className={`p-4 rounded-lg ${
        darkMode ? 'bg-gray-700' : 'bg-gray-50'
      }`}>
        <h4 className="font-semibold mb-3">Technical Indicators</h4>
        <div className="flex flex-wrap gap-4">
          {['sma', 'ema', 'rsi', 'macd', 'volume'].map(indicator => (
            <label key={indicator} className="flex items-center">
              <input
                type="checkbox"
                checked={indicators.includes(indicator)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setIndicators(prev => [...prev, indicator]);
                  } else {
                    setIndicators(prev => prev.filter(i => i !== indicator));
                  }
                }}
                className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">{indicator.toUpperCase()}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Chart Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <StatCard
          title="Current Price"
          value={chartData[chartData.length - 1]?.close || 0}
          format="currency"
          darkMode={darkMode}
        />
        <StatCard
          title="24h Change"
          value={calculate24hChange(chartData)}
          format="percentage"
          darkMode={darkMode}
          isPositive={calculate24hChange(chartData) >= 0}
        />
        <StatCard
          title="Volume"
          value={chartData.reduce((sum, item) => sum + item.volume, 0) / chartData.length}
          format="number"
          darkMode={darkMode}
          suffix=" avg"
        />
        <StatCard
          title="Volatility"
          value={calculateVolatility(chartData)}
          format="percentage"
          darkMode={darkMode}
        />
      </div>      

    </div>
  );
};

// Helper Components
const CustomTooltip = ({ active, payload, label, timeframe }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className={`p-3 rounded-lg shadow-lg ${
        document.body.classList.contains('dark') 
          ? 'bg-gray-800 border border-gray-600' 
          : 'bg-white border border-gray-200'
      }`}>
        <p className="font-bold">{formatTooltipDate(label, timeframe)}</p>
        <p>Open: <span className="font-semibold">${data.open?.toFixed(2)}</span></p>
        <p>High: <span className="font-semibold">${data.high?.toFixed(2)}</span></p>
        <p>Low: <span className="font-semibold">${data.low?.toFixed(2)}</span></p>
        <p>Close: <span className="font-semibold">${data.close?.toFixed(2)}</span></p>
        {data.volume && (
          <p>Volume: <span className="font-semibold">{data.volume.toLocaleString()}</span></p>
        )}
      </div>
    );
  }
  return null;
};

const StatCard = ({ title, value, format, darkMode, isPositive, suffix = '' }) => {
  const formatValue = (val, fmt) => {
    switch (fmt) {
      case 'currency':
        return `$${val.toFixed(2)}`;
      case 'percentage':
        return `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;
      case 'number':
        return val.toLocaleString() + suffix;
      default:
        return val.toString();
    }
  };

  return (
    <div className={`p-3 rounded-lg text-center ${
      darkMode ? 'bg-gray-700' : 'bg-gray-100'
    }`}>
      <div className="text-sm text-gray-500 mb-1">{title}</div>
      <div className={`text-lg font-bold ${
        format === 'percentage' || format === 'currency' 
          ? isPositive 
            ? darkMode ? 'text-green-400' : 'text-green-600'
            : darkMode ? 'text-red-400' : 'text-red-600'
          : darkMode ? 'text-white' : 'text-gray-900'
      }`}>
        {formatValue(value, format)}
      </div>
    </div>
  );
};

// Helper Functions
const getTimeframeLabel = (timeframe) => {
  const labels = {
    '1m': '1 Minute',
    '5m': '5 Minutes',
    '15m': '15 Minutes',
    '30m': '30 Minutes',
    '1h': '1 Hour',
    '4h': '4 Hours',
    '1d': 'Daily',
    '1w': 'Weekly',
    '1M': 'Monthly'
  };
  return labels[timeframe] || timeframe;
};

const formatXAxis = (value, timeframe) => {
  const date = new Date(value);
  switch (timeframe) {
    case '1m':
    case '5m':
    case '15m':
    case '30m':
      return date.toLocaleTimeString();
    case '1h':
    case '4h':
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    case '1d':
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    case '1w':
    case '1M':
      return date.toLocaleDateString([], { month: 'short', year: '2-digit' });
    default:
      return value;
  }
};

const formatTooltipDate = (value, timeframe) => {
  const date = new Date(value);
  switch (timeframe) {
    case '1m':
    case '5m':
    case '15m':
    case '30m':
      return date.toLocaleString([], { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    default:
      return date.toLocaleDateString([], { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
  }
};

const calculate24hChange = (data) => {
  if (data.length < 2) return 0;
  const current = data[data.length - 1].close;
  const previous = data[data.length - 2].close;
  return ((current - previous) / previous) * 100;
};

const calculateVolatility = (data) => {
  if (data.length < 2) return 0;
  const changes = data.slice(1).map((item, index) => 
    (item.close - data[index].close) / data[index].close
  );
  const avgChange = changes.reduce((sum, change) => sum + change, 0) / changes.length;
  const variance = changes.reduce((sum, change) => sum + Math.pow(change - avgChange, 2), 0) / changes.length;
  return Math.sqrt(variance) * 100; // Convert to percentage
};

const generateMockChartData = (count, timeframe) => {
  const data = [];
  let basePrice = 100 + Math.random() * 50;
  const baseVolume = 1000000;
  
  const now = new Date();
  let timeModifier = 0;
  
  // Set time modifier based on timeframe
  switch (timeframe) {
    case '1m': timeModifier = 60 * 1000; break;
    case '5m': timeModifier = 5 * 60 * 1000; break;
    case '15m': timeModifier = 15 * 60 * 1000; break;
    case '30m': timeModifier = 30 * 60 * 1000; break;
    case '1h': timeModifier = 60 * 60 * 1000; break;
    case '4h': timeModifier = 4 * 60 * 60 * 1000; break;
    case '1d': timeModifier = 24 * 60 * 60 * 1000; break;
    case '1w': timeModifier = 7 * 24 * 60 * 60 * 1000; break;
    case '1M': timeModifier = 30 * 24 * 60 * 60 * 1000; break;
    default: timeModifier = 60 * 1000;
  }
  
  for (let i = count - 1; i >= 0; i--) {
    const volatility = 0.5 + Math.random() * 2;
    const change = (Math.random() - 0.5) * volatility;
    
    const open = basePrice;
    const close = basePrice * (1 + change / 100);
    const high = Math.max(open, close) * (1 + Math.random() * 0.02);
    const low = Math.min(open, close) * (1 - Math.random() * 0.02);
    const volume = baseVolume * (0.8 + Math.random() * 0.4);
    
    data.push({
      time: new Date(now.getTime() - i * timeModifier).toISOString(),
      open,
      high,
      low,
      close,
      volume,
      isUp: close >= open,
      sma20: basePrice * (1 + (i > 20 ? 0.02 : 0)),
      ema12: basePrice * (1 + (i > 12 ? 0.015 : 0))
    });
    
    basePrice = close;
  }
  
  return data;
};

// // Helper function to generate mock chart data
// const generateMockChartData = (count, timeframe) => {
//   const data = [];
//   let basePrice = 100 + Math.random() * 50;
  
//   for (let i = 0; i < count; i++) {
//     const volatility = 0.5 + Math.random() * 2;
//     const change = (Math.random() - 0.5) * volatility;
    
//     const open = basePrice;
//     const close = basePrice * (1 + change);
//     const high = Math.max(open, close) * (1 + Math.random() * 0.1);
//     const low = Math.min(open, close) * (1 - Math.random() * 0.1);
//     const volume = 1000000 + Math.random() * 5000000;
    
//     data.push({
//       time: new Date(Date.now() - (count - i) * 60000).toISOString(),
//       open,
//       high,
//       low,
//       close,
//       volume,
//       ohlc: [open, high, low, close],
//       sma20: basePrice * (1 + (i > 20 ? 0.02 : 0))
//     });
    
//     basePrice = close;
//   }
  
//   return data;
// };

const formatChartData = (rawData) => {
  return rawData.map(item => ({
    time: new Date(item.time).getTime() / 1000,
    open: item.open,
    high: item.high,
    low: item.low,
    close: item.close,
  }));
};

export default AdvancedStockCharts;